import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { CustomerService } from '../../services/customer.service';
import { CreditService } from '../../services/credit.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { GlobalsService } from '../../services/globals.service';
import { PrintService } from '../../services/print.service';

interface RmaColumn {
	key: string;
	label: string;
	type: 'num' | 'str' | 'date';
	align: 'left' | 'right' | 'center';
}

@Component({
	selector: 'app-rma-log',
	templateUrl: './rma-log.component.html',
	styleUrls: ['./rma-log.component.scss'],
})
export class RmaLogComponent implements OnInit {

	// Same sessionStorage pattern as OpenPurchaseOrdersComponent — navigating away
	// (Edit details / Finalize credit both router.navigate elsewhere) and back used to
	// re-run ngOnInit and silently reset every filter to its default.
	static readonly RMA_LOG_FILTER_KEY = 'rmaLogFilters';

	@ViewChild('print_basic') tablecontentRef: ElementRef;

	columns: RmaColumn[] = [
		{ key: 'rma_no', label: 'RMA#', type: 'num', align: 'left' },
		{ key: 'date_created', label: 'Date', type: 'date', align: 'left' },
		{ key: 'customer', label: 'Customer', type: 'str', align: 'left' },
		{ key: 'enteredby', label: 'Entered By', type: 'str', align: 'left' },
		{ key: 'rma_state', label: 'Status', type: 'str', align: 'left' },
		{ key: 'credit_no', label: 'Credit Note#', type: 'num', align: 'left' },
		{ key: 'credit_total', label: 'Amount', type: 'num', align: 'right' },
		{ key: 'applied', label: 'Applied', type: 'num', align: 'right' },
		{ key: 'settled', label: 'Settled', type: 'str', align: 'center' },
	];

	rmas: any[] = [];
	sortField = 'rma_no';
	sortAsc = false;

	keywords = new UntypedFormControl('');
	enteredbyCtrl = new UntypedFormControl('');
	stateCtrl = new UntypedFormControl('all');
	fromdateCtrl = new UntypedFormControl('');
	todateCtrl = new UntypedFormControl('');

	state_filters = [
		{ value: 'all', name: 'All' },
		{ value: 'open', name: 'Open' },
		{ value: 'received', name: 'Received — Ready To Credit' },
		{ value: 'partial', name: 'Partially Received' },
		{ value: 'review', name: 'Needs Reconciling' },
		{ value: 'processed', name: 'Processed / Applied' },
		{ value: 'cancelled', name: 'Cancelled' },
	];

	sending = false;
	printing = false;
	color: any = '';
	config: any = null;

	orderdetails: any = null;

	constructor(
		private customerService: CustomerService,
		private creditService: CreditService,
		private globalSearchService: GlobalSearchService,
		private globalsService: GlobalsService,
		private printService: PrintService,
		private location: Location,
		private router: Router,
		private modalService: NgbModal,
	) {
		this.color = this.globalSearchService.getColor();
		this.config = this.globalsService.getApiConfig();
		this.applyStoredOrDefaultFilters();
	}

	// Restore whatever filters were active last time, so "Edit details" / "Finalize
	// credit" (both navigate away to another route) followed by Back doesn't land back
	// on default / empty search — only fall back to the default date window on a
	// genuinely fresh visit (nothing stored yet).
	private applyStoredOrDefaultFilters(): void {
		const restored = this.getStoredFilters();
		if (restored) {
			if (restored.datefrom) { this.fromdateCtrl.setValue(new Date(restored.datefrom)); }
			if (restored.dateto) { this.todateCtrl.setValue(new Date(restored.dateto)); }
			if (restored.enteredby != null) { this.enteredbyCtrl.setValue(restored.enteredby); }
			if (restored.state != null) { this.stateCtrl.setValue(restored.state); }
			if (restored.keywords != null) { this.keywords.setValue(restored.keywords); }
			return;
		}
		const now = new Date();
		if (this.config && this.config.env && this.config.env.client === 'km') {
			// KM asked to see everything by default — April 1 of the current year through today.
			this.fromdateCtrl.setValue(new Date(now.getFullYear(), 3, 1));
		} else {
			const from = new Date();
			from.setDate(from.getDate() - 30);
			this.fromdateCtrl.setValue(from);
		}
		this.todateCtrl.setValue(now);
	}

	getStoredFilters(): { datefrom: string; dateto: string; enteredby: string; state: string; keywords: string } | null {
		try {
			const raw = sessionStorage.getItem(RmaLogComponent.RMA_LOG_FILTER_KEY);
			return raw ? JSON.parse(raw) : null;
		} catch {
			return null;
		}
	}

	storeFilters(filters: { datefrom: string; dateto: string; enteredby: string; state: string; keywords: string }): void {
		try {
			sessionStorage.setItem(RmaLogComponent.RMA_LOG_FILTER_KEY, JSON.stringify(filters));
		} catch {}
	}

	ngOnInit(): void {
		this.loadRmas();
	}

	loadRmas() {
		this.sending = true;
		const filters = {
			datefrom: this.formatDate(this.fromdateCtrl.value),
			dateto: this.formatDate(this.todateCtrl.value),
			enteredby: this.enteredbyCtrl.value,
			state: this.stateCtrl.value,
			keywords: this.keywords.value,
		};
		this.storeFilters(filters);

		this.customerService.getRmaLog(filters).subscribe((results: any) => {
			this.rmas = Array.isArray(results) ? results : [];
			this.applySort();
			this.sending = false;
		});
	}

	// Normalize a Date/string to YYYY-MM-DD for the server date window.
	private formatDate(d: any): string {
		if (!d) { return ''; }
		const date = (d instanceof Date) ? d : new Date(d);
		if (isNaN(date.getTime())) { return ''; }
		const y = date.getFullYear();
		const m = ('0' + (date.getMonth() + 1)).slice(-2);
		const day = ('0' + date.getDate()).slice(-2);
		return `${y}-${m}-${day}`;
	}

	setSort(key: string) {
		if (this.sortField === key) {
			this.sortAsc = !this.sortAsc;
		} else {
			this.sortField = key;
			this.sortAsc = true;
		}
		this.applySort();
	}

	private applySort() {
		const col = this.columns.find(c => c.key === this.sortField);
		const type = col ? col.type : 'str';
		const dir = this.sortAsc ? 1 : -1;
		this.rmas = [...this.rmas].sort((a, b) => {
			let av = a[this.sortField];
			let bv = b[this.sortField];
			if (type === 'num') {
				return ((parseFloat(av) || 0) - (parseFloat(bv) || 0)) * dir;
			}
			if (type === 'date') {
				const at = av ? new Date(av).getTime() : 0;
				const bt = bv ? new Date(bv).getTime() : 0;
				return (at - bt) * dir;
			}
			av = (av || '').toString().toLowerCase();
			bv = (bv || '').toString().toLowerCase();
			return av.localeCompare(bv) * dir;
		});
	}

	// Clicking a summary chip narrows the status filter and reloads.
	quickState(state: string) {
		this.stateCtrl.setValue(state);
		this.loadRmas();
	}

	pillClass(state: string): string {
		switch ((state || '').toLowerCase()) {
			case 'processed': return 'pill--processed';
			case 'open': return 'pill--open';
			case 'received': return 'pill--received';
			case 'partial': return 'pill--partial';
			case 'part credited': return 'pill--partial';
			case 'cancelled': return 'pill--cancelled';
			default: return 'pill--review';
		}
	}

	/** "12 / 40" received-so-far indicator; blank when there's nothing to show (no lines, or not yet received). */
	qtyReceivedLabel(r: any): string {
		const total = parseFloat(r.rma_qty) || 0;
		if (!total) { return ''; }
		const recd = parseFloat(r.rma_qty_received) || 0;
		return recd + ' / ' + total;
	}

	/** PGS receipt#(s) + date behind the received figure, e.g. "PGS 100907 · 7/2/2026". */
	receiptLabel(r: any): string {
		if (!r.pgs_receipts) { return ''; }
		const when = r.pgs_last_receipt ? new Date(r.pgs_last_receipt + 'T12:00:00').toLocaleDateString() : '';
		return 'PGS ' + r.pgs_receipts + (when ? ' · ' + when : '');
	}

	/** The specific scanned lines that don't reconcile to the RMA — what a human has to fix. */
	reviewReasons(r: any): string[] {
		return Array.isArray(r.review_reasons) ? r.review_reasons : [];
	}

	/** Read-only line-level preview in a modal — same shape/endpoint as the Open RMAs list's "View". */
	previewCredit(r: any, content: any): void {
		this.creditService.getOrder(r.rma_no, false, true).subscribe((order: any) => {
			this.orderdetails = order;
			this.modalService.open(content, { ariaLabelledBy: 'modal-title', size: 'xl' }).result.then(() => {}, () => {});
		});
	}

	/** Open the RMA for editing (creditorders, not the debtortrans credit). */
	editDetails(r: any): void {
		this.router.navigate(['/receivables/credit-rma/' + r.rma_no]);
	}

	/** Same screen the Open RMAs list uses to apply/finalize the credit against the customer's account. */
	finalizeCredit(r: any): void {
		this.router.navigate(['/receivables/dispatch/' + r.rma_no]);
	}

	/** Not offered once a credit's actually been issued (Processed) — cancelling the RMA afterward
	 *  wouldn't undo that credit, just orphan it. Same cancel endpoint the Open RMAs list uses. */
	cancelRma(r: any, force: boolean = false): void {
		if (!force && !confirm('Cancel RMA #' + r.rma_no + '? This cannot be undone from here.')) { return; }
		this.creditService.cancelOrder(r.rma_no, force).subscribe((result: any) => {
			// The return is cancelled at PGS too when it was already sent. PGS locks an
			// inbound once receiving starts and rejects the cancel — nothing changes here
			// unless the operator deliberately forces it.
			if (result && result.success === false && result.pgs_blocked) {
				if (confirm('PGS would not cancel this return: ' + (result.message || 'rejected')
					+ '\n\nCancel it in QBC anyway? PGS will still receive the goods.')) {
					this.cancelRma(r, true);
				}
				return;
			}
			if (result && result.success === false) {
				this.globalSearchService.showNotification('RMA #' + r.rma_no + ' not cancelled: '
					+ (result.message || 'unknown error'), 'danger', 'bottom', 'right');
				return;
			}
			const pgsNote = (result && result.pgs && !result.pgs.skipped && result.pgs.message)
				? ' ' + result.pgs.message + '.'
				: '';
			this.globalSearchService.showNotification('RMA #' + r.rma_no + ' cancelled.' + pgsNote, 'success', 'bottom', 'right');
			this.loadRmas();
		});
	}

	countState(state: string): number {
		return this.rmas.filter(r => (r.rma_state || '').toLowerCase() === state.toLowerCase()).length;
	}

	sumAmount(): number {
		return this.rmas.reduce((acc, r) => acc + (parseFloat(r.credit_total) || 0), 0);
	}

	back(): void {
		this.location.back();
	}

	pdf() {
		this.sending = true;
		this.printing = true;
		const encoded: string = this.globalSearchService.base64encode(this.tablecontentRef.nativeElement.innerHTML);
		const data = { content: encoded, landscape: true, filename: 'RMA-Log.pdf' };
		this.printService.pdf(data).subscribe((result: any) => {
			this.globalSearchService.downloadPdf(result.content, data.filename);
			this.sending = false;
			this.printing = false;
		});
	}

	excel() {
		const encoded: string = this.globalSearchService.base64encode(this.tablecontentRef.nativeElement.innerHTML);
		const data = { content: encoded, filename: 'RMA-Log.xlsx' };
		this.printService.xls(data).subscribe((result: any) => {
			this.globalSearchService.downloadXls(result.content, data.filename);
			this.sending = false;
			this.printing = false;
		});
	}
}
